import {
  Button,
  ButtonAdd,
  ButtonIcon,
  Checkbox,
  CheckboxField,
  Col,
  InputField,
  LabelValuePairs,
  ReactForm,
  Row,
  Section,
  Table,
} from "mta-rkvr-styles";
import React, { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { get, put, remove } from "../api/fetch";
import { UserNotificationsRequestBody } from "../api/NotificationsSubscriptionsRequestTypes";
import { UserNotificationsResponseBody } from "../api/NotificationsSubscriptionsResponseTypes";
import CurrentUserContext from "../CurrentUserContext";
import { FieldName, Input } from "../types/notificationsSubscriptions/NotificationsSubscriptions";
import { toLocaleDate } from "../util/conversionUtils";
import { canUserEditAreasOfInterest } from "../util/rightsManagers/notificationsSubscription/editRightsManager";
import AddInterestAreaModal from "./AddInterestAreaModal";
import { emailRegexp } from "../util/validationHelper";
import { canRoleEditNotificationsSubscriptionValues } from "../util/rightsManagers/admin/editRightsManager";
import { RepresentationRight } from "../types/admin/User";

const NotificationsSubscription: React.FC<{
  id: number;
  refreshUser?: Function;
  othersRepRights?: RepresentationRight[];
}> = ({ id, refreshUser, othersRepRights }) => {
  const { t } = useTranslation();
  const tBase = "notificationsSubscriptions.";

  const navigate = useNavigate();

  const { id: currentUserId, usageRights, representationRights } = useContext(CurrentUserContext);

  const [rawData, setRawData] = useState<UserNotificationsResponseBody>();
  const [data, setData] = useState<{
    contacts: {
      labels: string[];
      values: ReactNode[];
    };
    notifications: {
      labels: string[];
      values: ReactNode[];
    };
  }>();

  const [isEdit, setEdit] = useState(false);
  const toggleEdit = () => {
    setEdit(!isEdit);
  };

  const [refreshObservable, setRefreshObservable] = useState(false);
  const refresh = () => {
    setRefreshObservable(!refreshObservable);
  };

  const [isModal, setModal] = useState(false);
  const openModal = () => {
    setModal(!isModal);
  };

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const togglePopoverOpen = () => {
    setIsPopoverOpen(!isPopoverOpen);
  };

  useEffect(() => {
    get<UserNotificationsResponseBody>({
      endPoint: "userNotifications",
      pathParams: [id],
    }).then((res) => {
      if (res.ok) {
        setRawData(res.body);
      }
    });
  }, [refreshObservable]);

  useEffect(() => {
    if (rawData) {
      const contactLabels: string[] = [];
      const contactValues: ReactNode[] = [];

      contactLabels.push(t(tBase + "contacts." + FieldName.email));
      contactValues.push(
        isEdit ? (
          <InputField
            name={FieldName.email}
            inlineEdit
            maxLength={100}
            value={rawData.epost}
            validations={{
              maxLength: 100,
              matchRegexp: emailRegexp,
            }}
            validationErrors={{
              maxLength: t("common.error.tooManyLetters"),
              matchRegexp: t("common.error.emailFormat"),
            }}
          />
        ) : (
          rawData.epost
        ),
      );

      contactLabels.push(t(tBase + "contacts." + FieldName.phoneNumber));
      contactValues.push(
        isEdit ? (
          <InputField
            name={FieldName.phoneNumber}
            inlineEdit
            maxLength={50}
            validations={"isPositiveInt"}
            validationError={t("common.error.numberNotPositiveInt")}
            value={rawData.telefon}
          />
        ) : (
          rawData.telefon
        ),
      );

      const notificationLabels: string[] = [];
      const notificationValues: ReactNode[] = [];

      notificationLabels.push(t(tBase + "notifications." + FieldName.m4));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m4} value={rawData.m4} unWrapped />
        ) : (
          <Checkbox checked={rawData.m4} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m2));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m2} value={rawData.m2} unWrapped />
        ) : (
          <Checkbox checked={rawData.m2} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m8));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m8} value={rawData.m8} unWrapped />
        ) : (
          <Checkbox checked={rawData.m8} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m3));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m3} value={rawData.m3} unWrapped />
        ) : (
          <Checkbox checked={rawData.m3} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m11));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m11} value={rawData.m11} unWrapped />
        ) : (
          <Checkbox checked={rawData.m11} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m13));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m13} value={rawData.m13} unWrapped />
        ) : (
          <Checkbox checked={rawData.m13} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m15));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m15} value={rawData.m15} unWrapped />
        ) : (
          <Checkbox checked={rawData.m15} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m17));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m17} value={rawData.m17} unWrapped />
        ) : (
          <Checkbox checked={rawData.m17} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m20));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m20} value={rawData.m20} unWrapped />
        ) : (
          <Checkbox checked={rawData.m20} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m19));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m19} value={rawData.m19} unWrapped />
        ) : (
          <Checkbox checked={rawData.m19} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m23));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m23} value={rawData.m23} unWrapped />
        ) : (
          <Checkbox checked={rawData.m23} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m25));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m25} value={rawData.m25} unWrapped />
        ) : (
          <Checkbox checked={rawData.m25} disabled />
        ),
      );

      // col 2 start

      notificationLabels.push(t(tBase + "notifications." + FieldName.submitter));
      notificationValues.push(rawData.muutja);

      notificationLabels.push(t(tBase + "notifications." + FieldName.m5));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m5} value={rawData.m5} unWrapped />
        ) : (
          <Checkbox checked={rawData.m5} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m6));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m6} value={rawData.m6} unWrapped />
        ) : (
          <Checkbox checked={rawData.m6} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m9));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m9} value={rawData.m9} unWrapped />
        ) : (
          <Checkbox checked={rawData.m9} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m1));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m1} value={rawData.m1} unWrapped />
        ) : (
          <Checkbox checked={rawData.m1} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m12));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m12} value={rawData.m12} unWrapped />
        ) : (
          <Checkbox checked={rawData.m12} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m14));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m14} value={rawData.m14} unWrapped />
        ) : (
          <Checkbox checked={rawData.m14} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m16));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m16} value={rawData.m16} unWrapped />
        ) : (
          <Checkbox checked={rawData.m16} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m18));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m18} value={rawData.m18} unWrapped />
        ) : (
          <Checkbox checked={rawData.m18} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m21));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m21} value={rawData.m21} unWrapped />
        ) : (
          <Checkbox checked={rawData.m21} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m22));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m22} value={rawData.m22} unWrapped />
        ) : (
          <Checkbox checked={rawData.m22} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m24));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m24} value={rawData.m24} unWrapped />
        ) : (
          <Checkbox checked={rawData.m24} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.m26));
      notificationValues.push(
        isEdit ? (
          <CheckboxField name={FieldName.m26} value={rawData.m26} unWrapped />
        ) : (
          <Checkbox checked={rawData.m26} disabled />
        ),
      );

      notificationLabels.push(t(tBase + "notifications." + FieldName.submitted));
      notificationValues.push(toLocaleDate(rawData.muudetud));

      setData({
        contacts: { labels: contactLabels, values: contactValues },
        notifications: {
          labels: notificationLabels,
          values: notificationValues,
        },
      });
    }
  }, [rawData, isEdit]);

  const modify = (input: Input) => {
    const {
      email,
      m1,
      m2,
      m3,
      m4,
      m5,
      m6,
      m8,
      m9,
      m11,
      m12,
      m13,
      m14,
      m15,
      m16,
      m17,
      m18,
      m19,
      m20,
      m21,
      m22,
      m23,
      m24,
      m25,
      m26,
      phoneNumber,
    } = input;
    put<UserNotificationsRequestBody, never>({
      endpoint: "userNotifications",
      pathParams: [id],
      requestBody: {
        epost: email,
        telefon: phoneNumber,
        m1,
        m2,
        m3,
        m4,
        m5,
        m6,
        m8,
        m9,
        m11,
        m12,
        m13,
        m14,
        m15,
        m16,
        m17,
        m18,
        m19,
        m20,
        m21,
        m22,
        m23,
        m24,
        m25,
        m26,
      },
    }).then((res) => {
      if (res.ok) {
        setEdit(false);
        refresh();
      }
    });
  };

  const deleteAreaOfInterest = (id: string) => {
    remove<never>({ endpoint: "userInterestArea", pathParams: [id] }).then((res) => {
      if (res.ok) {
        refresh();
      }
    });
  };

  const canEdit = useMemo(() => {
    return (
      id === currentUserId ||
      canRoleEditNotificationsSubscriptionValues(
        usageRights,
        othersRepRights != undefined &&
          representationRights.some((right) => othersRepRights.some((oigus) => oigus.organisatsioonId === right)),
      )
    );
  }, [id, othersRepRights]);

  return (
    <>
      <Section title={t(tBase + "title")} withSettings onEdit={!isEdit && canEdit ? toggleEdit : undefined}>
        {data && (
          <ReactForm onValidSubmit={(input: Input) => modify(input)} onReset={() => setEdit(false)}>
            <Section title={t(tBase + "contacts.title")}>
              <LabelValuePairs labelSize={4} labels={data.contacts.labels} values={data.contacts.values} isSplit />
            </Section>
            <Section title={t(tBase + "notifications.title")}>
              <LabelValuePairs
                labelSize={4}
                labels={data.notifications.labels}
                values={data.notifications.values}
                isSplit
              />
            </Section>
            {isEdit && (
              <Row>
                <Col className="align-right">
                  <Button className={"mr-1"} label={t("common.cancel")} type="reset" />
                  <Button className={"mr-1"} label={t("common.save")} type={"submit"} />
                </Col>
              </Row>
            )}
          </ReactForm>
        )}
      </Section>
      {rawData?.m1 && (
        <Row>
          <Col className="col-6">
            <Table
              id={t(tBase + "areasOfInterest.title")}
              title={t(tBase + "areasOfInterest.title")}
              headers={[t(tBase + "areasOfInterest.ehakName")]}
              content={rawData?.huvipiirkonnad?.map((piirkond) => {
                return { nimetus: piirkond.ehakNimetus };
              })}
              translate={t}
              noRowsMessage={t(tBase + "areasOfInterest.noRowsMessage")}
              editable
              newRowButton={
                canUserEditAreasOfInterest(id, currentUserId) ? (
                  <ButtonAdd
                    label={t(`${tBase}areasOfInterest.add.title`)}
                    onAdd={() => {
                      setModal(true);
                    }}
                  />
                ) : undefined
              }
              rowIds={rawData?.huvipiirkonnad?.map((piirkond) => piirkond.id)}
              customEditRowComponent={(obj: any) => {
                return canUserEditAreasOfInterest(id, currentUserId) ? (
                  <ButtonIcon
                    iconName={"icon-delete"}
                    id={"id" + obj.rowId}
                    popover={{
                      title: t(`${tBase}areasOfInterest.confirmDelete`, {
                        name: obj.data.name,
                      }),
                      placement: "top",
                      content: (
                        <Row>
                          <Col className="align-center">
                            <Button className={"mr-1"} label={t("common.no")} onClick={togglePopoverOpen} />
                            <Button
                              className={"mr-1"}
                              label={t("common.yes")}
                              onClick={() => deleteAreaOfInterest(obj.rowId)}
                            />
                          </Col>
                        </Row>
                      ),
                    }}
                  />
                ) : null;
              }}
            />
          </Col>
        </Row>
      )}
      {id !== currentUserId && (
        <Button
          onClick={() => {
            refreshUser && refreshUser();
            navigate(-1);
          }}
        >
          Tagasi
        </Button>
      )}
      {isModal && <AddInterestAreaModal isOpen={isModal} open={openModal} refresh={refresh} />}
    </>
  );
};

export default NotificationsSubscription;
